// Maintenance Demo Steps Configuration
// This file contains all demo steps for pages under /Pages/Maintenance/

export const maintenanceIndexSteps = [
  {
    title: "Welcome to Maintenance Management!",
    content: `
      <p class="mb-3">This is your Maintenance dashboard where you can manage property maintenance requests.</p>
      <p>Let's explore how to handle maintenance efficiently!</p>
    `,
    info: "Good maintenance management keeps tenants happy and properties in great condition.",
    target: null
  },
  {
    title: "Add Maintenance Property",
    content: `
      <p class="mb-3">Use this <strong>Add Property</strong> button to add properties to your maintenance management system.</p>
      <p>This enables maintenance request tracking.</p>
    `,
    info: "Properties must be added here before tenants can submit maintenance requests.",
    target: "add-property-btn",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Maintenance Properties List",
    content: `
      <p class="mb-3">View all properties enrolled in maintenance management:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>Property Address:</strong> Location details</li>
        <li><strong>Active Requests:</strong> Current maintenance issues</li>
        <li><strong>Status:</strong> Maintenance Task status</li>
      </ul>
    `,
    info: "Click on any property to see detailed maintenance history and active requests.",
    target: "properties-table"
  },
  {
    title: "Maintenance Requests",
    content: `
      <p class="mb-3">Track and manage <strong>maintenance requests</strong> from tenants:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>View request details and photos</li>
        <li>Track progress and completion</li>
        <li>Communicate with tenants</li>
      </ul>
    `,
    info: "Quick response to maintenance requests improves tenant satisfaction.",
    target: "maintenance-requests",
    modalOffset: { x: -40, y: 0 }
  }
]

export const maintenanceCreateSteps = [
  {
    title: "Welcome to Add Maintenance Property!",
    content: `
      <p class="mb-3">Add a property to your maintenance management system.</p>
      <p>This enables tenant maintenance requests for this property.</p>
    `,
    info: "Once added, tenants can submit maintenance requests directly through the system.",
    target: null
  },
  {
    title: "Use Existing Property",
    content: `
      <p class="mb-3">Select from your <strong>existing properties</strong> to quickly populate property details.</p>
      <p>This saves time by auto-filling address and owner information.</p>
    `,
    info: "If the property isn't listed, you can add it manually using the address lookup below.",
    target: "use-existing-address"
  },
  {
    title: "Owner/Landlord Name",
    content: `
      <p class="mb-3">Enter the <strong>Owner/Landlord Name</strong> for maintenance coordination.</p>
      <p>This contact will receive maintenance notifications and updates.</p>
    `,
    info: "Ensure this contact information is current for effective communication.",
    target: "owner_name",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Property Address Lookup",
    content: `
      <p class="mb-3">Use <strong>Address Lookup</strong> to find and set the property location.</p>
      <p>Accurate addresses help locate the property quickly.</p>
    `,
    info: "Include postal code for the most accurate address matching.",
    target: "address_lookup",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Invite Tenant",
    content: `
      <p class="mb-3">You can <strong>invite your tenant</strong> to join the portal at this stage.</p>
      <p>Inviting the tenant is <em>optional</em> but <strong>recommended</strong>, as it allows them to:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Submit maintenance requests online</li>
        <li>Track progress and updates in real time</li>
      </ul>
    `,
    info: "Inviting tenants improves communication and streamlines the maintenance workflow.",
    target: "invite-tenant"
  },
  {
    title: "Emergency Contact Information",
    content: `
      <p class="mb-3">Provide <strong>emergency contact</strong> details for urgent maintenance issues:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>24/7 emergency phone number</li>
        <li>Backup emergency contact</li>
        <li>After-hours procedures</li>
      </ul>
    `,
    info: "Emergency contacts are crucial for handling urgent maintenance situations.",
    target: "emergency-contact",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Save Property",
    content: `
      <p class="mb-3">Click <strong>Save</strong> to add the property to your maintenance system.</p>
      <p>Tenants will then be able to submit maintenance requests for this property.</p>
    `,
    info: "You can modify these settings anytime from the property management page.",
    target: "save-button",
    modalOffset: { x: -40, y: -40 }
  }
]

export const maintenanceEditSteps = [
  {
    title: "Edit Maintenance Property",
    content: `
      <p class="mb-3">Update your property's maintenance management settings.</p>
      <p>Keep contact information and preferences current for smooth operations.</p>
    `,
    info: "Regular updates ensure effective maintenance coordination.",
    target: null
  },
  {
    title: "Use Existing Property",
    content: `
      <p class="mb-3">Select from your <strong>existing properties</strong> to quickly populate property details.</p>
      <p>This saves time by auto-filling address and owner information.</p>
    `,
    info: "If the property isn't listed, you can add it manually using the address lookup below.",
    target: "use-existing-address"
  },
  {
    title: "Owner/Landlord Name",
    content: `
      <p class="mb-3">Enter the <strong>Owner/Landlord Name</strong> for maintenance coordination.</p>
      <p>This contact will receive maintenance notifications and updates.</p>
    `,
    info: "Ensure this contact information is current for effective communication.",
    target: "owner_name",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Property Address Lookup",
    content: `
      <p class="mb-3">Use <strong>Address Lookup</strong> to find and set the property location.</p>
      <p>Accurate addresses help locate the property quickly.</p>
    `,
    info: "Include postal code for the most accurate address matching.",
    target: "address_lookup",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Invite Tenant",
    content: `
      <p class="mb-3">You can <strong>invite your tenant</strong> to join the portal at this stage.</p>
      <p>Inviting the tenant is <em>optional</em> but <strong>recommended</strong>, as it allows them to:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Submit maintenance requests online</li>
        <li>Track progress and updates in real time</li>
      </ul>
    `,
    info: "Inviting tenants improves communication and streamlines the maintenance workflow.",
    target: "invite-tenant"
  },
  {
    title: "Emergency Contact Information",
    content: `
      <p class="mb-3">Provide <strong>emergency contact</strong> details for urgent maintenance issues:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>24/7 emergency phone number</li>
        <li>Backup emergency contact</li>
        <li>After-hours procedures</li>
      </ul>
    `,
    info: "Emergency contacts are crucial for handling urgent maintenance situations.",
    target: "emergency-contact",
    modalOffset: { x: -40, y: 0 }
  },
  {
    title: "Update Property",
    content: `
      <p class="mb-3">Click <strong>Update</strong> to add the property to your maintenance system.</p>
      <p>Tenants will then be able to submit maintenance requests for this property.</p>
    `,
    info: "You can modify these settings anytime from the property management page.",
    target: "save-button",
    modalOffset: { x: -40, y: -40 }
  }
]

export const maintenanceEmergencyOptionSteps = [
  {
    title: "Emergency Maintenance Options",
    content: `
      <p class="mb-3">Configure how <strong>emergency maintenance requests</strong> are handled.</p>
      <p>Emergency situations require immediate attention and special procedures.</p>
    `,
    info: "Clear emergency procedures protect both properties and tenants.",
    target: null
  },
  {
    title: "Emergency Classifications",
    content: `
      <p class="mb-3">Define what constitutes <strong>emergency maintenance</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Water leaks and flooding</li>
        <li>Heating/cooling failures</li>
        <li>Electrical hazards</li>
        <li>Security issues</li>
      </ul>
    `,
    info: "Clear definitions help tenants identify true emergencies.",
    target: "emergency-classifications"
  },
  {
    title: "Emergency Response Procedures",
    content: `
      <p class="mb-3">Set up <strong>emergency response procedures</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>24/7 contact numbers</li>
        <li>Response time commitments</li>
        <li>Cost approval limits</li>
      </ul>
    `,
    info: "Quick emergency response prevents small problems from becoming major issues.",
    target: "emergency-procedures"
  },
  {
    title: "After-Hours Contacts",
    content: `
      <p class="mb-3">Provide <strong>after-hours emergency contacts</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Emergency hotline numbers</li>
        <li>On-call maintenance staff</li>
        <li>Property management contacts</li>
        <li>Backup emergency services</li>
      </ul>
    `,
    info: "Multiple contact options ensure emergencies are always addressed.",
    target: "after-hours-contacts"
  }
]

export const maintenanceSubscriptionIndexSteps = [
  {
    title: "Maintenance Subscription Plans",
    content: `
      <p class="mb-3">Choose a <strong>maintenance subscription plan</strong> that fits your property portfolio.</p>
      <p>Different plans offer varying levels of service and features.</p>
    `,
    info: "The right subscription plan can save money and improve maintenance efficiency.",
    target: null
  },
  {
    title: "Plan Comparison",
    content: `
      <p class="mb-3">Compare available <strong>subscription plans</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li><strong>Basic:</strong> Essential maintenance tracking</li>
        <li><strong>Professional:</strong> Vendor network access</li>
        <li><strong>Premium:</strong> Full-service maintenance management</li>
      </ul>
    `,
    info: "Higher-tier plans include more vendor options and priority support.",
    target: "plan-comparison"
  },
  {
    title: "Features and Benefits",
    content: `
      <p class="mb-3">Review <strong>plan features</strong> and benefits:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Number of properties included</li>
        <li>Vendor network access</li>
        <li>Response time guarantees</li>
        <li>Reporting and analytics</li>
      </ul>
    `,
    info: "Choose features that match your property management needs.",
    target: "plan-features"
  },
  {
    title: "Select Your Plan",
    content: `
      <p class="mb-3">Choose and activate your <strong>maintenance subscription</strong>.</p>
      <p>You can upgrade or change plans anytime as your needs evolve.</p>
    `,
    info: "Start with a plan that fits your current needs - you can always upgrade later.",
    target: "select-plan"
  }
]

export const maintenanceTermsAndConditionsSteps = [
  {
    title: "Maintenance Service Terms",
    content: `
      <p class="mb-3">Review the <strong>terms and conditions</strong> for maintenance services.</p>
      <p>Understanding these terms helps ensure smooth service delivery.</p>
    `,
    info: "Take time to review these terms - they govern how maintenance services work.",
    target: null
  },
  {
    title: "Service Agreements",
    content: `
      <p class="mb-3">Understand the <strong>service agreements</strong> including:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Response time commitments</li>
        <li>Quality guarantees</li>
        <li>Liability coverage</li>
        <li>Dispute resolution</li>
      </ul>
    `,
    info: "These agreements protect both you and the service providers.",
    target: "service-agreements"
  },
  {
    title: "Payment Terms",
    content: `
      <p class="mb-3">Review <strong>payment terms</strong> for maintenance services:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Subscription billing cycles</li>
        <li>Per-service charges</li>
        <li>Payment methods accepted</li>
        <li>Refund policies</li>
      </ul>
    `,
    info: "Clear payment terms help avoid billing surprises.",
    target: "payment-terms"
  },
  {
    title: "Accept Terms",
    content: `
      <p class="mb-3">Accept the terms and conditions to <strong>activate maintenance services</strong>.</p>
      <p>This enables full access to the maintenance management platform.</p>
    `,
    info: "You must accept these terms to use the maintenance management features.",
    target: "accept-terms"
  }
]

export const maintenancePropertiesSubscriptionIndexSteps = [
  {
    title: "Rent Collection Subscription",
    content: `
      <p class="mb-3">Manage your <strong>rent collection subscription</strong> and billing.</p>
      <p>Different plans offer varying features and property limits.</p>
    `,
    info: "Choose a plan that fits your property portfolio size and needs.",
    target: null
  },
  {
    title: "Current Plan Details",
    content: `
      <p class="mb-3">View your <strong>current subscription plan</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Plan name and features</li>
        <li>Number of properties included</li>
        <li>Monthly/annual pricing</li>
        <li>Next billing date</li>
      </ul>
    `,
    info: "Your plan determines how many properties can use online rent collection.",
    target: "current-plan"
  },
  {
    title: "Billing Management",
    content: `
      <p class="mb-3">Manage your <strong>subscription billing</strong>:</p>
      <ul class="list-disc ml-5 space-y-1">
        <li>Update payment methods</li>
        <li>View billing history</li>
        <li>Change billing frequency</li>
        <li>Download invoices</li>
      </ul>
    `,
    info: "Keep billing information current to avoid service interruptions.",
    target: "billing-management"
  }
]